export enum ESplashTone {
  LIGHT = 'light',
  DARK = 'dark',
  /** A background we cannot read; the shadowed light ring reads on either. */
  UNKNOWN = 'unknown',
}

/** What the document paints when the theme names no background — see layout.tsx. */
const DEFAULT_DOCUMENT_BACKGROUND = '#fff';

/**
 * The relative luminance above which a dark ring contrasts more than a light
 * one: where WCAG's contrast ratios against black and against white are equal.
 */
const DARK_RING_LUMINANCE_THRESHOLD = 0.179;

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB_COLOR = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i;

type RgbChannels = [number, number, number];

export const getSplashTone = (backgroundColor: string | undefined): ESplashTone => {
  const channels = parseColor(backgroundColor?.trim() || DEFAULT_DOCUMENT_BACKGROUND);
  if (!channels) {
    return ESplashTone.UNKNOWN;
  }

  return getRelativeLuminance(channels) > DARK_RING_LUMINANCE_THRESHOLD
    ? ESplashTone.DARK
    : ESplashTone.LIGHT;
};

const parseColor = (color: string): RgbChannels | undefined =>
  parseHexColor(color) ?? parseRgbColor(color);

const parseHexColor = (color: string): RgbChannels | undefined => {
  const digits = HEX_COLOR.exec(color)?.[1];
  if (!digits) {
    return undefined;
  }

  const fullDigits =
    digits.length === 3 ? digits.split('').map((digit) => digit + digit).join('') : digits;

  return [0, 2, 4].map((offset) =>
    parseInt(fullDigits.slice(offset, offset + 2), 16)
  ) as RgbChannels;
};

const parseRgbColor = (color: string): RgbChannels | undefined => {
  const match = RGB_COLOR.exec(color);
  if (!match) {
    return undefined;
  }

  return [match[1], match[2], match[3]].map(Number) as RgbChannels;
};

const getRelativeLuminance = ([red, green, blue]: RgbChannels): number =>
  0.2126 * toLinearChannel(red) + 0.7152 * toLinearChannel(green) + 0.0722 * toLinearChannel(blue);

const toLinearChannel = (channel: number): number => {
  const normalized = channel / 255;

  return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
};
